import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import axios from 'axios'
import { API_CONFIG, loadAppConfig } from '../../config/apiConfig'
import { showToast } from "../../utils/messageUtil"
import { useUser } from '../../contexts/UserContext'
import { handleJSAPIAccess } from '../../utils/auth_access_util'
import TimeRangePicker from '../time-select/TimeRangePicker'
import LocationSearchInput from '../../components/LocationSearchInput'
import './index.css'

export default function PrivateCarApply() {
    const navigate = useNavigate()
    const { userInfo, loading: userLoading, isNewFeatureExperience, checkingNewFeatureExperience, checkNewFeatureExperienceStatus } = useUser()
    const [showTimeRangePicker, setShowTimeRangePicker] = useState(false)

    // 手机端（≤768px）检测：用车时间框改用「月日时分」短格式，减少宽度防溢出
    const [isMobileWidth, setIsMobileWidth] = useState(
        typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches
    )
    useEffect(() => {
        const mq = window.matchMedia('(max-width: 768px)')
        const onMqChange = (e) => setIsMobileWidth(e.matches)
        if (mq.addEventListener) mq.addEventListener('change', onMqChange)
        else mq.addListener(onMqChange)
        return () => {
            if (mq.removeEventListener) mq.removeEventListener('change', onMqChange)
            else mq.removeListener(onMqChange)
        }
    }, [])

    // 用车时间框显示格式：手机端月日时分（MM-DD HH:mm），桌面端年月日时分
    const formatBoxDateTime = (dateTimeStr) => {
        if (!dateTimeStr) return '';
        const s = dateTimeStr.slice(0, 16).replace('T', ' ');
        return isMobileWidth ? s.slice(5) : s;
    };
    const [submitting, setSubmitting] = useState(false)

    // 本地时间格式化为 "YYYY-MM-DDTHH:mm:00"（与 dispatch 页面保持一致）
    const formatLocalTime = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}:00`;
    };

    // 首次进入页面（无地图返回恢复数据）时的默认表单数据：
    // 开始时间 = 当前本地时间，结束时间 = 开始时间 + 最小用车时长（租户配置，默认 60 分钟）
    const getInitialFormData = () => {
        const now = new Date();
        const startTime = formatLocalTime(now);
        const minMinutes = API_CONFIG.minApplicationMinutes || 60;
        const endTime = new Date(now.getTime() + minMinutes * 60 * 1000);
        return {
            plateNumber: '',
            imei: '',
            expectedStartTime: startTime,
            expectedEndTime: formatLocalTime(endTime),
            expectedLocation: '',
            destination: '',
            reason: '',
            passengers: '',
            passengersCode: '',
            // 申请人三件套：自动从 userInfo 带出，联系电话/部门允许修改，姓名不允许修改
            // 初值留空，下方 useEffect 在 userInfo 就绪后回填
            applicantName: '',
            applicantPhone: '',
            applicantDept: ''
        };
    };
    const [formData, setFormData] = useState(getInitialFormData)
    // 多目的地：主目的地存 formData.destination，额外目的地存 extraDestinations（最多5个），提交时用中文逗号拼接
    const [extraDestinations, setExtraDestinations] = useState([])
    const [additionalDestination, setAdditionalDestination] = useState('')
    const [showAddDestination, setShowAddDestination] = useState(false)
    // IMEI 验证状态：手动「验证」按钮或扫码自动验证通过（设备在线）后为 true，提交拦截依据
    const [imeiVerified, setImeiVerified] = useState(false)
    // 联系电话格式校验状态：失焦时校验，不通过显示提示；提交时再校验一次（不允许格式错误提交）
    const [phoneInvalid, setPhoneInvalid] = useState(false)

    // 联系电话失焦校验：手机号格式 / 1[3-9]开头、11位数字
    const handleApplicantPhoneBlur = () => {
        const phone = (formData.applicantPhone || '').trim()
        if (!phone) { setPhoneInvalid(false); return }
        const ok = /^1[3-9]\d{9}$/.test(phone)
        setPhoneInvalid(!ok)
        if (!ok) showToast('手机号格式不正确，应为11位数字、1[3-9]开头', 'error')
    }

    // 权限守卫：仅新功能体验名单用户可访问（白名单校验）
    useEffect(() => {
        if (userLoading) return
        const uid = userInfo?.open_id || userInfo?.user_id
        if (uid) checkNewFeatureExperienceStatus(uid)
    }, [userLoading, userInfo, checkNewFeatureExperienceStatus])

    // 【2026-08-22 开放所有用户】移除新功能体验名单守卫（功能全量开放，不再弹回首页）
    /* useEffect(() => {
        if (userLoading || checkingNewFeatureExperience) return
        if (!isNewFeatureExperience) {
            showToast('您不在新功能体验名单中，不能访问', 'error')
            navigate('/', { replace: true })
        }
    }, [userLoading, checkingNewFeatureExperience, isNewFeatureExperience, navigate]) */

    // 申请人三件套自动带出：userInfo 就绪后回填到 formData（仅在字段为空时填，避免覆盖用户已修改的内容）
    useEffect(() => {
        if (userLoading || !userInfo) return
        const phoneFromUserInfo = (userInfo.mobile || '').replace(/^\+86/, '')
        setFormData(prev => {
            const patch = {}
            if (!prev.applicantName && userInfo.name) patch.applicantName = userInfo.name
            if (!prev.applicantPhone && phoneFromUserInfo) patch.applicantPhone = phoneFromUserInfo
            if (!prev.applicantDept && userInfo.department_name) patch.applicantDept = userInfo.department_name
            return Object.keys(patch).length ? { ...prev, ...patch } : prev
        })
    }, [userLoading, userInfo])

    // 从地图选点返回时：恢复已保存的表单数据 + 回填出发地/目的地（map-select 会写入 fromLocation/toLocation/selectedLocation）
    useEffect(() => {
        // 0. 【私车公干首次进入】主动鉴权 JSAPI，避免首次打开联系人选择报"鉴权失败"
        // 派车能开是因为之前用过已鉴权（__JSAPI_AUTHORIZED__ 模块级 true 保留），私车首次打开需先鉴权
        handleJSAPIAccess((ok) => console.log('[private-car-apply] JSAPI鉴权:', ok))
        // 1. 恢复跳地图前保存的表单（避免页面重挂载导致已填内容丢失）
        let restoredForm = null
        let restoredExtras = []
        const saved = sessionStorage.getItem('privateCarApplyForm')
        if (saved) {
            try {
                const parsed = JSON.parse(saved)
                restoredForm = parsed.formData || null
                restoredExtras = Array.isArray(parsed.extraDestinations) ? parsed.extraDestinations : []
            } catch (error) {
                console.error('解析私车申请表单失败:', error)
            }
            sessionStorage.removeItem('privateCarApplyForm')
        }

        // 2. 读取地图返回的地点信息（同时兼容 selectedLocation 与 fromLocation/toLocation 两个 key）
        let returnedFrom = null
        let returnedTo = null
        const selectedLocation = sessionStorage.getItem('selectedLocation')
        if (selectedLocation) {
            try {
                const locationData = JSON.parse(selectedLocation)
                if (locationData.type === 'from') returnedFrom = locationData.location?.name || null
                else if (locationData.type === 'to') returnedTo = locationData.location?.name || null
            } catch (error) {
                console.error('解析地点信息失败:', error)
            }
            sessionStorage.removeItem('selectedLocation')
        }
        const storedFromLocation = sessionStorage.getItem('fromLocation')
        if (storedFromLocation) {
            returnedFrom = storedFromLocation
            sessionStorage.removeItem('fromLocation')
        }
        const storedToLocation = sessionStorage.getItem('toLocation')
        if (storedToLocation) {
            returnedTo = storedToLocation
            sessionStorage.removeItem('toLocation')
        }

        // 3. 合并计算目的地（主目的地 + 额外目的地）
        //    主目的地已有值时，从地图再次选点视为"改选"：替换主目的地，不追加为额外目的地
        let finalDestination = (restoredForm && restoredForm.destination) || ''
        let finalExtras = restoredExtras
        if (returnedTo) {
            finalDestination = returnedTo
        }

        setFormData(prev => ({
            ...prev,
            ...(restoredForm || {}),
            expectedLocation: returnedFrom || (restoredForm && restoredForm.expectedLocation) || '',
            destination: finalDestination
        }))
        setExtraDestinations(finalExtras)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // "YYYY-MM-DDTHH:mm:00" → Date（用于 TimeRangePicker 初始值）
    const parseFormTime = (str) => {
        if (!str) return new Date();
        const d = new Date(str);
        return isNaN(d.getTime()) ? new Date() : d;
    };

    // TimeRangePicker 确认：同时更新开始/结束时间，并校验最小时长
    const handleTimeRangeConfirm = (startDateTime, endDateTime) => {
        setShowTimeRangePicker(false);
        if (startDateTime && endDateTime) {
            const minMinutes = API_CONFIG.minApplicationMinutes || 60;
            const durationMinutes = (endDateTime.getTime() - startDateTime.getTime()) / 60000;
            if (durationMinutes < minMinutes) {
                endDateTime = new Date(startDateTime.getTime() + minMinutes * 60000);
                showToast(`用车时长不能低于${minMinutes}分钟，已自动延长结束时间`, 'warning');
            }
        }
        const toLocalInput = (d) => {
            if (!d || isNaN(d.getTime())) return '';
            const pad = n => String(n).padStart(2, '0');
            return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:00`;
        };
        setFormData(prev => ({
            ...prev,
            expectedStartTime: toLocalInput(startDateTime),
            expectedEndTime: toLocalInput(endDateTime)
        }));
    };

    const handleChange = (e) => {
        const { name, value } = e.target
        setFormData(prev => ({ ...prev, [name]: value }))
        // 用户修改 IMEI 后需重新验证，重置验证状态
        if (name === 'imei') setImeiVerified(false)
    }

    // 出发地/目的地已改为输入框内联地图检索（LocationSearchInput），不再跳转地图选择页面

    // 添加额外目的地（最多5个额外目的地，含主目的地共6个）
    const handleAddDestination = () => {
        if (extraDestinations.length >= 5) {
            showToast('目的地最多只能添加5个', 'error')
            return
        }
        const value = (additionalDestination || '').trim()
        if (!value) {
            showToast('请输入目的地', 'error')
            return
        }
        if (formData.destination.trim() === value || extraDestinations.includes(value)) {
            showToast('目的地已存在，不能重复添加', 'error')
            return
        }
        setExtraDestinations(prev => [...prev, value])
        setAdditionalDestination('')
        setShowAddDestination(false)
    }

    // 删除单个目的地（index 0 为主目的地，其余为额外目的地）
    const handleRemoveDestination = (index) => {
        if (formData.destination && index === 0) {
            setFormData(prev => ({ ...prev, destination: '' }))
            return
        }
        const extraIndex = formData.destination ? index - 1 : index
        if (extraIndex < 0) return
        setExtraDestinations(prev => prev.filter((_, i) => i !== extraIndex))
    }

    // 打开飞书联系人选择器（最多6个同行人）
    const openLarkContactPicker = async () => {
        console.log('openLarkContactPicker 被调用');
        console.log('window.h5sdk:', window.h5sdk);
        console.log('window.tt:', window.tt);

        // 确保JSAPI鉴权完成
        const isJSAPIAuthorized = await new Promise((resolve) => {
            if (window.h5sdk && window.h5sdk.config) {
                // 重新进行JSAPI鉴权
                handleJSAPIAccess((isSuccess) => {
                    resolve(isSuccess);
                });
            } else {
                resolve(false);
            }
        });

        if (!isJSAPIAuthorized) {
            showToast('飞书JSAPI鉴权失败，无法打开联系人选择', 'error');
            return;
        }

        const tt = window.h5sdk.tt || window.tt;
        console.log('tt对象:', tt);
        console.log('tt.chooseContact:', tt?.chooseContact);

        if (!tt || !tt.chooseContact) {
            showToast('当前飞书版本不支持联系人选择', 'error');
            return;
        }

        console.log('开始调用 tt.chooseContact');
        tt.chooseContact({
            multi: true,
            ignore: false,
            maxNum: 6,
            limitTips: "最多只能选择6个同行人",
            externalContact: false,
            enableChooseDepartment: false,
            success: (res) => {
                handleContactSelectResult(res);
            },
            fail: (res) => {
                // 用户取消选择或关闭选择器，不报错
                console.log('取消选择同行人:', res);
            }
        });
    };

    // 打开飞书扫码：扫码获取设备 IMEI 编号（与联系人选择同模式：先 JSAPI 鉴权再调 tt.scanCode）
    const handleScanImei = async () => {
        try {
            // 先做 JSAPI 鉴权（与 dispatch 通讯录选择同模式）
            const isAuthorized = await new Promise((resolve) => {
                if (window.h5sdk && window.h5sdk.config) {
                    handleJSAPIAccess((ok) => resolve(ok));
                } else { resolve(false); }
            });
            if (!isAuthorized) {
                showToast('飞书JSAPI鉴权失败，无法打开扫码', 'error');
                return;
            }
            const tt = window.h5sdk.tt || window.tt;
            if (!tt || !tt.scanCode) {
                showToast('当前环境不支持扫码', 'error');
                return;
            }
            tt.scanCode({
                scanType: ['barCode'],  // 仅扫条形码（IMEI 是条形码）
                success: async (res) => {
                    const code = res && (res.scanResult || res.result || '');
                    if (code) {
                        // 校验：IMEI 15 位数字，简单校验
                        const imei = String(code).trim();
                        if (!/^\d{15,17}$/.test(imei)) {
                            showToast(`扫到内容不是有效IMEI：${imei}`, 'error');
                            return;
                        }
                        setFormData(prev => ({ ...prev, imei }));
                        showToast('扫码成功，已填入设备编号', 'success');
                        // 扫码后自动校验设备在线状态（18gps 官方规则：server_time-heart_time ≤ 25 分钟在线）
                        // 设备在线 → 自动置为已验证
                        const ok = await validateScannedImei(imei);
                        setImeiVerified(!!ok);
                    } else {
                        showToast('未识别到内容', 'error');
                    }
                },
                fail: (err) => {
                    // 用户取消或扫码失败
                    console.log('扫码取消/失败:', err);
                }
            });
        } catch (e) {
            console.error('扫码异常:', e);
            showToast('扫码异常', 'error');
        }
    };

    // 调用后端校验设备在线状态（18gps 官方规则：server_time-heart_time ≤ 25 分钟在线；速度=9 且离线>25分钟未启用；server_time>expire_date 已过期）
    // 扫码自动验证与手动「验证」按钮共用：返回 Promise<boolean> —— 设备在线返回 true；离线/未知/未启用/已过期/接口异常返回 false（toast 已输出具体原因）
    const validateScannedImei = async (imei) => {
        try {
            const response = await axios.post(`${API_CONFIG.apiBaseUrl}/carApplication/validatePrivateCarImei`, {
                apiToken: API_CONFIG.apiToken,
                companyId: API_CONFIG.companyId,
                tenantId: API_CONFIG.id || localStorage.getItem('tenantId') || undefined,
                imei
            }, {
                headers: { 'Content-Type': 'application/json' }
            })
            const d = (response.data && response.data.data) || {};
            let msg = '设备状态未知';
            if (d.status === 'online') {
                msg = `设备在线${d.deviceName ? '：' + d.deviceName : ''}${d.offlineMinutes ? '（离线 ' + d.offlineMinutes + ' 分钟）' : ''}`;
            } else if (d.status === 'offline') {
                msg = `设备离线 ${d.offlineMinutes ? '（已离线 ' + d.offlineMinutes + ' 分钟）' : ''}`;
            }
            if (d.isNotActivated) msg += '（未启用）';
            if (d.isExpired) msg += '（已过期）';
            showToast(msg, d.status === 'online' ? 'success' : 'error');
            return d.status === 'online';
        } catch (error) {
            console.error('校验设备状态失败:', error)
            showToast(`设备状态校验失败: ${error.message || '网络错误'}，请重试`, 'error')
            return false;
        }
    };

    // 手动「验证」按钮：非空 + 15-17 位数字校验后调用后端，设备在线才通过
    const handleVerifyImei = async () => {
        const imei = (formData.imei || '').trim();
        if (!imei) { showToast('请先输入设备编号', 'error'); return; }
        if (!/^\d{15,17}$/.test(imei)) { showToast('设备编号格式不正确（应为15位数字）', 'error'); return; }
        // 复用 validateScannedImei：在线返回 true，离线/未知/未启用/已过期/接口异常返回 false
        const ok = await validateScannedImei(imei);
        setImeiVerified(!!ok);
        if (ok) showToast('设备验证通过', 'success');
    };

    // 处理联系人选择结果：排除自己、去重、最多6人，追加到已有同行人
    const handleContactSelectResult = (res) => {
        console.log('处理联系人选择结果:', res);

        if (res.data && res.data.length > 0) {
            const selectedUsers = res.data;
            const myOpenId = userInfo?.open_id || userInfo?.user_id
            const existingNames = (formData.passengers || '').split(',').filter(n => n.trim())
            const existingCodes = (formData.passengersCode || '').split(',').filter(c => c.trim())
            let selfFiltered = 0
            let dupFiltered = 0
            const newNames = []
            const newCodes = []
            selectedUsers.forEach(user => {
                const userOpenId = user.open_id || user.openId || user.user_id
                const name = user.name
                if (!userOpenId || !name) return
                // 不能选择自己作为同行人
                if (myOpenId && userOpenId === myOpenId) {
                    selfFiltered++
                    return
                }
                // 去重：已选过的同行人不重复添加
                if (existingCodes.includes(userOpenId) || newCodes.includes(userOpenId)) {
                    dupFiltered++
                    return
                }
                // 最多6人
                if (existingCodes.length + newCodes.length >= 6) return
                newNames.push(name)
                newCodes.push(userOpenId)
            })

            if (selfFiltered > 0) showToast('不能选择自己作为同行人', 'error')
            if (dupFiltered > 0) showToast('已排除已选择的同行人', 'error')
            if (newNames.length === 0) {
                if (selfFiltered === 0 && dupFiltered === 0) showToast('同行人最多只能选择6人', 'error')
                return
            }

            setFormData(prev => ({
                ...prev,
                passengers: [...existingNames, ...newNames].join(','),
                passengersCode: [...existingCodes, ...newCodes].join(',')
            }))
            showToast(`已选择 ${newNames.length} 位同行人`, 'success')
        } else {
            console.warn('未选择联系人');
            showToast('未选择联系人', 'error');
        }
    };

    // 删除单个同行人（同步删除姓名和openId）
    const removePassenger = (index) => {
        setFormData(prev => {
            const names = (prev.passengers || '').split(',').filter(n => n.trim());
            const codes = (prev.passengersCode || '').split(',').filter(c => c.trim());
            if (index < 0 || index >= names.length) return prev;
            names.splice(index, 1);
            codes.splice(index, 1);
            return {
                ...prev,
                passengers: names.join(','),
                passengersCode: codes.join(',')
            };
        });
    };

    // 提交私车公干申请
    const handleSubmit = async () => {
        // 多目的地：主目的地 + 额外目的地，用中文逗号"，"拼接
        const finalDestination = [formData.destination, ...extraDestinations].filter(d => d && d.trim()).join('，')
        // 必填校验
        if (!formData.plateNumber.trim()) { showToast('请填写车牌号', 'error'); return }
        if (!formData.imei.trim()) { showToast('请填写设备编号', 'error'); return }
        // 设备须在线验证通过后才能提交（手动「验证」按钮或扫码自动验证）
        if (!imeiVerified) { showToast('请先验证设备（设备在线验证通过后才能提交）', 'error'); return }
        if (!formData.expectedStartTime || !formData.expectedEndTime) { showToast('请选择用车时间', 'error'); return }
        if (!formData.expectedLocation.trim()) { showToast('请选择出发地', 'error'); return }
        if (!finalDestination) { showToast('请选择目的地', 'error'); return }
        if (!formData.reason.trim()) { showToast('请填写用车原因', 'error'); return }
        // 申请人三件套：用户已修改的优先，未填的兜底从 userInfo 取（应已在 useEffect 自动带出）
        const finalApplicantName = (formData.applicantName || '').trim() || (userInfo?.name || '')
        const finalApplicantPhone = (formData.applicantPhone || '').trim() || ((userInfo?.mobile || '').replace(/^\+86/, ''))
        const finalApplicantDept = (formData.applicantDept || '').trim() || (userInfo?.department_name || '')
        if (!finalApplicantName) { showToast('申请人为空，请等待飞书用户信息加载', 'error'); return }
        if (!finalApplicantPhone) { showToast('联系电话不能为空', 'error'); return }
        // 联系电话格式校验：11位数字、1[3-9]开头
        if (!/^1[3-9]\d{9}$/.test(finalApplicantPhone)) {
            setPhoneInvalid(true)
            showToast('手机号格式不正确，应为11位数字、1[3-9]开头', 'error')
            return
        }
        if (!finalApplicantDept) { showToast('所属部门不能为空', 'error'); return }
        if (!userInfo) { showToast('用户信息未加载，请稍后重试', 'error'); return }

        setSubmitting(true)
        try {
            const requestData = {
                apiToken: API_CONFIG.apiToken,
                companyId: API_CONFIG.companyId,
                // 私车标识：1-私车
                applicationType: 1,
                plateNumber: formData.plateNumber.trim(),
                imei: formData.imei.trim(),
                // 申请人直接带出（私车公干无用车人选项，用车人=申请人本人）
                // 优先用用户在表单里修改后的值，否则用 userInfo 默认值
                applicantId: userInfo?.employee_no,
                applicantOpenId: userInfo.open_id,
                applicantName: finalApplicantName,
                applicantPhone: finalApplicantPhone,
                applicantDept: finalApplicantDept,
                applyTime: new Date().toISOString().replace('T', ' ').substring(0, 19),
                reason: formData.reason.trim(),
                expectedStartTime: formData.expectedStartTime.replace('T', ' '),
                expectedEndTime: formData.expectedEndTime.replace('T', ' '),
                expectedLocation: formData.expectedLocation.trim(),
                destination: finalDestination,
                // 同行人（逗号分隔姓名 / 逗号分隔openId，可为空）
                passengers: formData.passengers || '',
                passengersCode: formData.passengersCode || '',
                driveType: 1,
                isPersonal: 0, // 本人申请
                vehicleUserName: finalApplicantName,
                vehicleUserCode: userInfo.open_id || userInfo.user_id
            }

            const response = await axios.post(`${API_CONFIG.apiBaseUrl}/carApplication/submitPrivateCarApplication`, requestData, {
                headers: { 'Content-Type': 'application/json' }
            })
            if (response.data && response.data.code === 200) {
                showToast('提交成功，等待上级审批', 'success')
                navigate('/private-car-record')
            } else {
                showToast(`提交失败: ${response.data?.message || '未知错误'}`, 'error')
            }
        } catch (error) {
            console.error('提交私车公干失败:', error)
            showToast(`提交失败: ${error.message || '网络错误'}`, 'error')
        } finally {
            setSubmitting(false)
        }
    }

    // 展示用：全部目的地（主目的地 + 额外目的地）
    const allDestinations = [formData.destination, ...extraDestinations].filter(d => d && d.trim())

    return (
        <div className="private-car-apply-page">
            <div className="pca-header">
                {/*<button className="pca-back" onClick={() => navigate('/')}>← 返回</button>*/}
                <h2>私车公干</h2>
            </div>

            <div className="pca-form-card">
                {/* 申请人三件套：自动带出，与下方表单共用同一卡片；姓名只读，电话/部门必填可改 */}
                <div className="pca-form-item">
                    <span className="pca-label">申请人</span>
                    <input
                        className="pca-input pca-input-readonly"
                        name="applicantName"
                        value={formData.applicantName}
                        onChange={handleChange}
                        readOnly
                        placeholder="加载中..."
                        title="申请人姓名自动从飞书带出，不允许修改"
                    />
                </div>
                <div className="pca-form-item">
                    <span className="pca-label required">联系电话</span>
                    <input
                        className={`pca-input ${phoneInvalid ? 'pca-input-invalid' : ''}`}
                        name="applicantPhone"
                        type="tel"
                        inputMode="numeric"
                        maxLength={11}
                        value={formData.applicantPhone}
                        onChange={handleChange}
                        onBlur={handleApplicantPhoneBlur}
                        placeholder="请输入11位手机号"
                    />
                </div>
                {phoneInvalid && (
                    <div className="pca-form-tip">手机号格式不正确，应为11位数字、1[3-9]开头</div>
                )}
                <div className="pca-form-item">
                    <span className="pca-label required">所属部门</span>
                    <input
                        className="pca-input"
                        name="applicantDept"
                        value={formData.applicantDept}
                        onChange={handleChange}
                        placeholder="如：运维科"
                    />
                </div>

                {/* 车牌号：手填 */}
                <div className="pca-form-item">
                    <span className="pca-label required">车牌号:</span>
                    <input
                        className="pca-input"
                        name="plateNumber"
                        value={formData.plateNumber}
                        onChange={handleChange}
                        placeholder="请输入本人车牌号，如 粤T12345"
                    />
                </div>

                {/* 设备编号（IMEI）：手填 或 飞书扫码，必填 */}
                <div className="pca-form-item">
                    <span className="pca-label required">设备编号:</span>
                    <div className="pca-imei-row">
                        <input
                            className="pca-input"
                            name="imei"
                            value={formData.imei}
                            onChange={handleChange}
                            placeholder="请输入设备IMEI编号或扫码"
                            maxLength={50}
                        />
                        <button type="button" className="pca-scan-btn" onClick={handleScanImei}>扫码</button>
                        <button
                            type="button"
                            className={`pca-verify-btn ${imeiVerified ? 'verified' : ''}`}
                            onClick={handleVerifyImei}
                        >
                            {imeiVerified ? '已通过' : '验证'}
                        </button>
                    </div>
                </div>

                {/* 用车时间：合并为单个时间框（开始 → 结束），点击弹出 TimeRangePicker */}
                <div className="pca-form-item">
                    <span className="pca-label required">用车时间:</span>
                    <div className="pca-datetime-container">
                        <div className="pca-datetime-box pca-datetime-box-merged" onClick={() => setShowTimeRangePicker(true)} title="点击选择用车时间">
                            <span className="pca-datetime-box-label">开始</span>
                            <span className="pca-datetime-box-value">
                                {formData.expectedStartTime ? formatBoxDateTime(formData.expectedStartTime) : '请选择'}
                            </span>
                            <div className="pca-datetime-box-arrow">→</div>
                            <span className="pca-datetime-box-label">结束</span>
                            <span className="pca-datetime-box-value">
                                {formData.expectedEndTime ? formatBoxDateTime(formData.expectedEndTime) : '请选择'}
                            </span>
                        </div>
                    </div>
                </div>

                {/* 出发地：输入框内联地图检索 */}
                <div className="pca-form-item">
                    <span className="pca-label required">出发地:</span>
                    <div className="pca-location-box">
                        <LocationSearchInput
                            inputClassName="pca-input"
                            name="expectedLocation"
                            value={formData.expectedLocation}
                            onChange={(val) => handleChange({ target: { name: 'expectedLocation', value: val } })}
                            placeholder="请输入出发地"
                            style={{ flex: 1, minWidth: 0 }}
                        />
                    </div>
                </div>

                {/* 目的地：主目的地内联检索 + 多目的地标签 */}
                <div className="pca-form-item">
                    <span className="pca-label required">目的地:</span>
                    <div className="pca-location-box">
                        <LocationSearchInput
                            inputClassName="pca-input"
                            name="destination"
                            value={formData.destination}
                            onChange={(val) => handleChange({ target: { name: 'destination', value: val } })}
                            placeholder="请输入目的地"
                            style={{ flex: 1, minWidth: 0 }}
                        />
                    </div>
                    {allDestinations.length > 0 && (
                        <div className="pca-destinations-tags">
                            {allDestinations.map((dest, index) => (
                                <span key={index} className="pca-destination-tag">
                                    {dest}
                                    <button
                                        type="button"
                                        className="pca-remove-tag-btn"
                                        onClick={() => handleRemoveDestination(index)}
                                    >
                                        ×
                                    </button>
                                </span>
                            ))}
                        </div>
                    )}
                    <div className="pca-add-destination-wrap">
                        <button
                            type="button"
                            className={`pca-add-destination-btn ${extraDestinations.length >= 5 ? 'disabled' : ''}`}
                            disabled={extraDestinations.length >= 5}
                            onClick={() => {
                                if (extraDestinations.length >= 5) {
                                    showToast('目的地最多只能添加5个', 'error')
                                    return
                                }
                                setShowAddDestination(!showAddDestination)
                            }}
                        >
                            ➕ 添加目的地 ({extraDestinations.length}/5)
                        </button>
                    </div>
                    {showAddDestination && (
                        <div className="pca-additional-destination-container">
                            <LocationSearchInput
                                inputClassName="pca-input"
                                value={additionalDestination}
                                onChange={(val) => setAdditionalDestination(val)}
                                placeholder="请输入其他目的地"
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter') handleAddDestination()
                                }}
                            />
                            <button type="button" className="pca-confirm-add-btn" onClick={handleAddDestination}>确认添加</button>
                        </div>
                    )}
                </div>

                {/* 同行人：飞书联系人选择（最多6人，不含本人） */}
                <div className="pca-form-item">
                    <span className="pca-label">同行人:</span>
                    <div className="pca-location-box">
                        <input
                            className="pca-input pca-location-input"
                            name="passengers"
                            value={formData.passengers}
                            readOnly
                            placeholder="点击选择同行人（最多6人，不含本人）"
                            onClick={openLarkContactPicker}
                        />
                        <button type="button" className="pca-location-btn" onClick={openLarkContactPicker}>👥</button>
                    </div>
                    {formData.passengers && (
                        <div className="pca-passenger-tags">
                            {formData.passengers.split(',').filter(n => n.trim()).map((name, index) => (
                                <span key={index} className="pca-passenger-tag">
                                    {name}
                                    <button
                                        type="button"
                                        className="pca-passenger-remove"
                                        onClick={() => removePassenger(index)}
                                        title="删除该同行人"
                                    >
                                        ×
                                    </button>
                                </span>
                            ))}
                        </div>
                    )}
                </div>

                {/* 用车原因 */}
                <div className="pca-form-item">
                    <span className="pca-label required">用车原因:</span>
                    <textarea
                        className="pca-textarea"
                        name="reason"
                        value={formData.reason}
                        onChange={handleChange}
                        placeholder="请输入用车原因"
                        rows="3"
                    />
                </div>

                <div className="pca-btn-row">
                    <button className="pca-submit-btn" onClick={handleSubmit} disabled={submitting}>
                        {submitting ? '提交中...' : '提交申请'}
                    </button>
                    <button className="pca-cancel-btn" onClick={() => navigate('/')} disabled={submitting}>
                        取消
                    </button>
                </div>
            </div>

            <TimeRangePicker
                open={showTimeRangePicker}
                onClose={() => setShowTimeRangePicker(false)}
                initialStartTime={parseFormTime(formData.expectedStartTime)}
                initialEndTime={parseFormTime(formData.expectedEndTime)}
                onConfirm={handleTimeRangeConfirm}
            />
        </div>
    )
}
